import { useMemo } from 'react'

import type { api } from './api'
import { useSession } from './SessionContext'

/**
 * หน้า "พนักงาน" (/admin/employees) — คุยกับ /api/admin/employees
 * ฝั่ง backend อยู่ที่ backend/src/lib/employees.js ซึ่งอธิบายไว้ว่าตาราง Users
 * เป็นของ HR ที่ระบบอื่นใช้ร่วมกัน และกติกาการเขียนเข้มกว่าตารางอื่นเพราะอะไร
 *
 * ⚠️ ชื่อฟิลด์ที่นี่ต้องตรงกับ FIELDS ใน backend เป๊ะ — backend ปฏิเสธคีย์ที่ไม่รู้จัก
 *    ด้วย 400 โดยตั้งใจ (พิมพ์ชื่อผิดจะรู้ทันที ไม่ใช่กดบันทึกแล้วค่าไม่เปลี่ยนเงียบ ๆ)
 */

export const EMPLOYEES_BASE = '/api/admin/employees'

export type EmployeeStatus = 'active' | 'resigned' | 'disabled'

export const STATUS_LABEL: Record<EmployeeStatus, string> = {
  active: 'ทำงาน',
  resigned: 'ลาออก',
  disabled: 'ปิดใช้งาน',
}

/** แถวในตาราง — ตั้งใจไม่มีข้อมูลส่วนตัว (เลขบัตร/วันเกิด/รหัสผ่าน) ติดมาด้วย */
export type EmployeeRow = {
  empCode: string
  prename: string | null
  firstName: string | null
  lastName: string | null
  fullName: string | null
  departCode: string | null
  departName: string | null
  branchCode: string | null
  branchName: string | null
  functionCode: string | null
  functionName: string | null
  position: string | null
  companyCode: string | null
  buCode: string | null
  sex: string | null
  workStartDate: string | null
  resignDate: string | null
  disabled: boolean
  disabledPerson: boolean
  processTa: boolean
  canOt: boolean
  status: EmployeeStatus
}

/**
 * รายละเอียดของคนเดียว — มีครบทุกช่องที่แก้ได้
 *
 * `password` มาเฉพาะบัญชีที่มีสิทธิ์แก้ (viewer จะไม่มีคีย์นี้เลย ไม่ใช่ได้ค่าว่าง)
 * → เช็คด้วย `'password' in employee` ไม่ใช่ `employee.password !== ''`
 */
export type Employee = EmployeeRow & {
  userId: number
  empId: string
  username: string
  firstNameEng: string | null
  lastNameEng: string | null
  birthDate: string | null
  citizenId: string | null
  email: string | null
  phoneNo: string | null
  mcCard: string | null
  photo: string | null
  probationDate: string | null
  probationDay: number | null
  buDesc: string | null
  leaveApproveId: string | null
  leaveApproveName: string | null
  approve2: string | null
  approve2Name: string | null
  approve3: string | null
  approve3Name: string | null
  password?: string | null
}

/**
 * ตัวเลือกของ dropdown
 *
 * `known: false` = รหัสนี้มีคนใช้อยู่จริงแต่ **ไม่มีชื่อในตาราง Departments/Branch**
 * `inUse: true`  = ยังมีพนักงานที่ยังทำงานอยู่ถืออยู่
 *
 * ห้ามกรอง known=false ทิ้ง — ถ้าตัวเลือกหาย พอ admin กดบันทึกคนที่ถือรหัสนั้น
 * ค่าจะถูกล้างโดยไม่มีใครสังเกต (เหตุผลเต็มอยู่หัวไฟล์ฝั่ง backend)
 */
export type LookupItem = {
  code: string
  name: string | null
  companyCode: string | null
  known: boolean
  inUse: boolean
}

export type Lookups = {
  departments: LookupItem[]
  branches: LookupItem[]
  functions: LookupItem[]
  prenames: string[]
  sexes: string[]
  buLevels: { code: string; name: string | null }[]
  positions: string[]
  probationDays: number[]
  companyCodes: string[]
}

export type Approver = {
  code: string
  name: string | null
  position: string | null
  resigned: boolean
}

export type EmployeeQuery = {
  q?: string
  status?: 'active' | 'resigned' | 'all'
  departCode?: string
  branchCode?: string
  functionCode?: string
  /** กรองบริษัท — backend เช็คว่าอยู่ใน hrCompanyCodes ของ session ก่อนเสมอ */
  companyCode?: string
  page?: number
  pageSize?: number
  /** `คีย์:ทิศ,คีย์:ทิศ` — คีย์ที่รับได้ประกาศไว้ที่ backend/src/lib/employees.js */
  sort?: string
}

export type EmployeePage = {
  employees: EmployeeRow[]
  total: number
  page: number
  pageSize: number
}

/** ค่าที่ส่งไปบันทึก — ทุกคีย์ optional เพราะ PATCH แก้เฉพาะที่ส่งไป */
export type EmployeePayload = Partial<
  Pick<
    Employee,
    | 'empCode'
    | 'prename'
    | 'firstName'
    | 'lastName'
    | 'firstNameEng'
    | 'lastNameEng'
    | 'sex'
    | 'birthDate'
    | 'citizenId'
    | 'email'
    | 'phoneNo'
    | 'companyCode'
    | 'branchCode'
    | 'functionCode'
    | 'departCode'
    | 'position'
    | 'buCode'
    | 'workStartDate'
    | 'resignDate'
    | 'probationDate'
    | 'probationDay'
    | 'leaveApproveId'
    | 'approve2'
    | 'approve3'
    | 'mcCard'
    | 'photo'
    | 'disabled'
    | 'disabledPerson'
    | 'processTa'
    | 'canOt'
    | 'password'
  >
>

function qs(query: Record<string, string | number | undefined>): string {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === '') continue
    params.set(key, String(value))
  }
  const s = params.toString()
  return s ? `?${s}` : ''
}

const json = (body: unknown) => ({ body: JSON.stringify(body) })

/**
 * ⚠️ ต้องยิงผ่าน `call` ของ SessionContext เสมอ ห้ามเรียก `api` ตรง ๆ
 *    `call` เป็นที่เดียวที่ดัก 401 แล้วเคลียร์ session ให้ทั้งแอป — เรียกตรงจะได้
 *    หน้าที่ยังวาดข้อมูลเก่าอยู่ทั้งที่ล็อกอินหลุดไปแล้ว แล้วทุกปุ่มพังเงียบ ๆ
 */
export function employeesApi(call: typeof api) {
  return {
    list: (query: EmployeeQuery = {}) => call<EmployeePage>(`${EMPLOYEES_BASE}${qs(query)}`),

    get: (empCode: string) =>
      call<{ employee: Employee }>(`${EMPLOYEES_BASE}/${encodeURIComponent(empCode)}`).then(
        (res) => res.employee,
      ),

    lookups: () => call<Lookups>(`${EMPLOYEES_BASE}/lookups`),

    approvers: (q: string) =>
      call<{ approvers: Approver[] }>(`${EMPLOYEES_BASE}/approvers${qs({ q })}`).then(
        (res) => res.approvers ?? [],
      ),

    create: (payload: EmployeePayload) =>
      call<{ employee: Employee }>(EMPLOYEES_BASE, { method: 'POST', ...json(payload) }).then(
        (res) => res.employee,
      ),

    // รหัสพนักงานแก้ไม่ได้หลังสร้าง — backend ปฏิเสธด้วย 400 ถ้าส่งค่าที่ต่างไป
    update: (empCode: string, payload: EmployeePayload) =>
      call<{ employee: Employee }>(`${EMPLOYEES_BASE}/${encodeURIComponent(empCode)}`, {
        method: 'PATCH',
        ...json(payload),
      }).then((res) => res.employee),
  }
}

export type EmployeesApi = ReturnType<typeof employeesApi>

export function useEmployees(): EmployeesApi {
  const { call } = useSession()
  return useMemo(() => employeesApi(call), [call])
}

/* ── ตัวช่วยแสดงผล ─────────────────────────────────────────────────────────── */

/**
 * ชื่อที่แสดงบนหน้าจอ — ถ้าไม่มีชื่อเลยให้โชว์รหัสแทน "ไม่ระบุ"
 * (แถวว่างเปล่าอ่านไม่ออกว่าเป็นใคร แล้วกดเข้าไปดูก็ไม่รู้ว่าเปิดถูกคนไหม)
 */
export function displayName(e: { fullName: string | null; empCode: string }): string {
  return e.fullName?.trim() || e.empCode
}

/** ตัวอักษรในวงกลมหน้าชื่อ — ใช้ชื่อจริงตัวแรก ข้ามคำนำหน้า */
export function initial(e: { firstName: string | null; empCode: string }): string {
  const source = e.firstName?.trim() || e.empCode
  return source.slice(0, 1).toUpperCase()
}

/**
 * ป้ายชื่อของตัวเลือก — รหัสที่ไม่มีชื่อต้องบอกให้เห็นว่าผิดปกติ ไม่ใช่โชว์รหัสเปล่า ๆ
 * แล้วปล่อยให้คนเดาว่าทำไมอันนี้ไม่มีชื่อเหมือนเพื่อน
 */
export function lookupLabel(item: LookupItem): string {
  if (item.name) return `${item.code} · ${item.name}`
  return `${item.code} (ไม่มีชื่อในระบบ)`
}

/**
 * ตัวเลือกที่จะโชว์ใน dropdown
 *
 * ตัดรหัสที่ "ไม่มีชื่อ + ไม่มีใครใช้แล้ว" ออก เพราะเป็นซากของคนที่ลาออกไปนานแล้ว
 * (ของจริงมี 57 รหัส ถ้าโชว์หมดจะได้ 84 ตัวเลือกจนเลื่อนหาไม่เจอ)
 * แต่ **ค่าที่คนนี้ถืออยู่ต้องอยู่ในรายการเสมอ** ไม่งั้น <select> จะเด้งไปค่าอื่นเอง
 * แล้วกดบันทึกทีเดียวแผนกเปลี่ยนโดยไม่ได้ตั้งใจ
 */
export function optionsFor(items: LookupItem[], current: string | null): LookupItem[] {
  const shown = items.filter((i) => i.known || i.inUse || i.code === current)
  if (current && !shown.some((i) => i.code === current)) {
    shown.push({ code: current, name: null, companyCode: null, known: false, inUse: false })
  }
  return shown
}

export function errorText(err: unknown): string {
  return err instanceof Error ? err.message : String(err)
}
